import { Renderer2 } from '@angular/core';

/**
 * Checks whether a given URL is for an SVG symbol.
 *
 * @param url URL of SVG to load.
 */
export function isUrlSymbol(url: string): boolean {
    return url.charAt(0) === '#' || url.indexOf('.svg#') > -1;
}

/**
 * Checks whether SVGs are supported.
 */
export function isSvgSupported(): boolean {
    return typeof SVGRect !== 'undefined';
}

/**
 * Extracts the symbol from the given SVG into its own SVG element, using
 * a `<use>` tag.
 *
 * @param svg The SVG to get the symbol from.
 * @param symbolId The ID of the symbol.
 */
export function createSymbolSvg(renderer: Renderer2, svg: SVGElement, symbolId: string): SVGElement {
    const symbol = svg.querySelector(`[id="${symbolId}"]`);
    if (!symbol) {
        throw new Error(`Symbol "${symbolId}" not found`);
    }

    const elSvg = renderer.createElement('svg', 'svg');
    renderer.appendChild(elSvg, symbol);

    const elSvgUse = renderer.createElement('use', 'svg');
    renderer.setAttribute(elSvgUse, 'href', `#${symbolId}`, 'xlink');
    renderer.appendChild(elSvg, elSvgUse);

    return elSvg;
}

/**
 * Removes attributes from an element.
 *
 * @param element Element with attributes.
 * @param attrs Names of attributes to remove.
 */
export function removeAttributes(element: Element, attrs: Array<string>): void {
    for (const attr of attrs) {
        const elAttr = element.getAttribute(attr);
        if (elAttr) {
            element.removeAttribute(attr);
        }
    }

    // Child nodes
    const innerEls = element.getElementsByTagName('*');
    for (const i of innerEls as unknown as []) {
        removeAttributes(i, attrs);
    }
}

/**
 * Sets attributes on an element.
 *
 * @param element Element to set attributes on.
 * @param attrs Attribute key/values.
 */
export function setAttributes(element: Element, attrs: { [key: string]: any }): void {
    for (const [key, value] of Object.entries(attrs)) {
        element.setAttribute(key, value);
    }
}

export function makeIdsUnique(element: SVGElement) {
    const timestamp = `${new Date().getTime()}_${Math.round(Math.random() * 1000000)}`;
    const idElements = element.querySelectorAll('*[id]');
    const idMap = new Map<string, string>();

    idElements.forEach((childElement, key) => {
        const uniqueId = `id_${key}_${timestamp}`;
        idMap.set(childElement.id, uniqueId);
        childElement.id = uniqueId;
    });

    for (const [key, value] of idMap.entries()) {
        const replaceRegexp = new RegExp(`#${key}("|\\))`, 'g');
        element.innerHTML = element.innerHTML.replace(replaceRegexp, `#${value}$1`);
    }
    return element;
}
